import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { AccountSubscriptions } from '@/domain/protocols/AccountSubscriptions'
import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'

import type { IHttpClient } from '../http/IHttpClient'
import { AccountSubscriptionsRemote, AccountSubscriptionsResponseMapper } from '../Mappers/accountSubscriptions'
import { AccountUserRemote, AccountUserResponseMapper } from '../Mappers/authentication'
import { IAccountWebClient } from './IAccountWebClient'

import { NEXATLAS_API_BASE } from '@/infra/http/apiBaseUrls'

interface UsersMeResponse {
  user: AccountUserRemote
  capabilities?: string[]
}

/**
 * Statuses com que a API do Account recusa o token: `401` para token inválido
 * (`authorizationTokenIsInvalid`) e `403` quando a requisição vai sem o header `Authorization`.
 */
const TOKEN_REJECTED_STATUSES = [401, 403]

@injectable()
export class AccountWebClient implements IAccountWebClient {
  constructor(
    @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState
  ) {}

  async init(): Promise<Result<void>> {
    return this.fetchAccount()
  }

  /**
   * Busca o usuário e as capabilities em `GET /v2/users/me` e grava os dois no `AuthState`. Só a recusa
   * do token (`401`/`403`) vira `ACCOUNT_TOKEN_ERROR`, que apaga o cookie e manda para o login; qualquer
   * outra falha (rede, `5xx`, resposta sem `user`) vira `ACCOUNT_UNAVAILABLE`, porque mandar para o login
   * não resolveria. Em falha, o `AuthState` fica como estava.
   */
  async fetchAccount(): Promise<Result<void>> {
    const response = await this.getFromAccount<UsersMeResponse>('/v2/users/me')
    if (response.isFailure) return Result.fail(response.error)

    const body = response.getValue()
    const user = AccountUserResponseMapper(body?.user)
    if (!user) return Result.fail('ACCOUNT_UNAVAILABLE')

    const capabilities = Array.isArray(body.capabilities) ? body.capabilities : []
    this.authState.setAccount(user, capabilities)

    return Result.ok()
  }

  /**
   * Busca assinaturas e licenças em `GET /v2/users/me/subscriptions`. Não grava nada no `AuthState`: o
   * dado só é lido pela tela de Configurações. Falhas seguem a mesma classificação de `fetchAccount`.
   */
  async getSubscriptions(): Promise<Result<AccountSubscriptions>> {
    const response = await this.getFromAccount<AccountSubscriptionsRemote>('/v2/users/me/subscriptions')
    if (response.isFailure) return Result.fail(response.error)

    const subscriptions = AccountSubscriptionsResponseMapper(response.getValue())
    if (!subscriptions) return Result.fail('ACCOUNT_UNAVAILABLE')

    return Result.ok(subscriptions)
  }

  /**
   * `GET` na API do Account com o token do cookie. A recusa do token (`401`/`403`) vira
   * `ACCOUNT_TOKEN_ERROR`; rede e qualquer outro status fora de `2xx`, `ACCOUNT_UNAVAILABLE`.
   */
  private async getFromAccount<T>(path: string): Promise<Result<T>> {
    const response = await this.httpClient.get<T>(`${NEXATLAS_API_BASE}${path}`, {
      headers: {
        Authorization: `Token ${this.authState.getStateSnapshot().token}`
      }
    })

    if (response.success === false) {
      return Result.fail('ACCOUNT_UNAVAILABLE')
    }

    if (TOKEN_REJECTED_STATUSES.includes(response.status)) {
      return Result.fail('ACCOUNT_TOKEN_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      return Result.fail('ACCOUNT_UNAVAILABLE')
    }

    return Result.ok(response.response)
  }
}
